/* Tayshaba gift repair. Shipping prices remain in Shopify shipping settings. */
(() => {
  'use strict';
  if (window.TayshabaGiftGuard) return;

  // Existing bottle product and gift variant mappings from main-cart-items.liquid.
  // One matching gift per bottle unit. Only designated gift variant IDs are managed.
  // Ordinary paid sample variants are untouched, regardless of their scent or price.
  const rules = [
    { bottleProductId: 8641421115708, giftVariantId: 55218531008885 },
    { bottleProductId: 8641419804988, giftVariantId: 55218531762549 },
    { bottleProductId: 8641418363196, giftVariantId: 55218533990773 },
    { bottleProductId: 8641415840060, giftVariantId: 55218537791861 },
    { bottleProductId: 8641413185852, giftVariantId: 55218544148853 }
  ];
  const rawFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const french = document.documentElement.lang.toLowerCase().startsWith('fr');
  const messages = french ? {
    error: 'Impossible de vérifier votre panier. Veuillez réessayer avant de passer commande.',
    terms: 'Veuillez accepter les conditions générales avant de passer commande.',
    empty: 'Votre panier est vide. Ajoutez un produit pour passer commande.',
    stock: 'Un échantillon offert est indisponible. Votre bouteille reste dans le panier.'
  } : {
    error: 'We could not verify your basket. Please try again before checking out.',
    terms: 'Please accept the terms and conditions before checking out.',
    empty: 'Your basket is empty. Add a product before checking out.',
    stock: 'A complimentary sample is unavailable. Your bottle remains in the basket.'
  };
  let queue = Promise.resolve();
  let externalWrites = 0;
  let running = null;
  let timer;
  let editUntil = 0;
  let checkingOut = false;
  let renderNeeded = false;
  let lastCart = null;
  let acceptedTerms = false;
  let writeRevision = 0;
  const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

  function serialFetch(input, init) {
    const task = queue.then(async () => {
      const response = await rawFetch(input, init);
      // Wait for the complete response without consuming the theme's response.
      await response.clone().text();
      return response;
    });
    queue = task.catch(() => {});
    return task;
  }

  function isMutation(input, init) {
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
      const method = (init?.method || input?.method || 'GET').toUpperCase();
      return url.origin === location.origin && method === 'POST' &&
        /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
    } catch (_) { return false; }
  }

  window.fetch = function(input, init) {
    if (!isMutation(input, init)) return rawFetch(input, init);
    externalWrites++;
    writeRevision++;
    return serialFetch(input, init).finally(() => {
      externalWrites--;
      renderNeeded = true;
      schedule();
    });
  };

  async function json(path, body) {
    const init = { credentials: 'same-origin', cache: 'no-store' };
    if (body) Object.assign(init, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const response = body
      ? await serialFetch(root + path, init)
      : await rawFetch(root + path, init);
    const value = await response.json();
    if (!response.ok || value.errors || (value.status && value.status >= 400)) {
      throw new Error(value.description || 'Cart request failed');
    }
    return value;
  }

  function isBottle(item, rule) {
    if (Number(item.product_id) !== rule.bottleProductId || item.quantity < 1) return false;
    if (rules.some(gift => Number(item.variant_id || item.id) === gift.giftVariantId)) return false;
    // Retain product mapping for Default Title bottles. Explicit sizes must be 100 ml.
    const options = (item.variant_options || [item.variant_title || '']).join(' ');
    const sizes = [...options.matchAll(/\b(\d+(?:[.,]\d+)?)\s*ml\b/gi)];
    return !sizes.length || sizes.some(match => Number(match[1].replace(',', '.')) === 100);
  }

  function entitlement(cart, rule) {
    return cart.items.filter(item => isBottle(item, rule))
      .reduce((total, item) => total + Number(item.quantity), 0);
  }

  function plan(cart, unavailable = new Set()) {
    // First remove only quantities that exceed the matching bottle entitlement.
    for (const rule of rules) {
      let remaining = entitlement(cart, rule);
      const gifts = cart.items.filter(item => Number(item.variant_id || item.id) === rule.giftVariantId);
      for (const gift of gifts) {
        const allowed = Math.min(Number(gift.quantity), remaining);
        if (Number(gift.quantity) !== allowed) {
          return { path: 'cart/change.js', body: { id: gift.key, quantity: allowed } };
        }
        remaining -= allowed;
      }
    }
    // Then top up missing gifts, without altering any paid sample variant.
    for (const rule of rules) {
      if (unavailable.has(rule.giftVariantId)) continue;
      const gifts = cart.items.filter(item => Number(item.variant_id || item.id) === rule.giftVariantId);
      const missing = entitlement(cart, rule) - gifts.reduce((total, item) => total + Number(item.quantity), 0);
      if (missing > 0) {
        if (gifts.length) {
          return { path: 'cart/change.js', body: { id: gifts[0].key, quantity: Number(gifts[0].quantity) + missing }, addId: rule.giftVariantId };
        }
        return { path: 'cart/add.js', body: { items: [{ id: rule.giftVariantId, quantity: missing }] }, addId: rule.giftVariantId };
      }
    }
    return null;
  }

  function notice(message) {
    const hosts = [document.querySelector('#main-cart-footer'), document.querySelector('.mini-cart__footer')].filter(Boolean);
    if (!hosts.length) hosts.push(document.body);
    for (const host of hosts) {
      let box = host.querySelector('[data-tayshaba-cart-notice]');
      if (!message) { box?.remove(); continue; }
      if (!box) {
        box = document.createElement('p');
        box.setAttribute('data-tayshaba-cart-notice', '');
        box.setAttribute('role', 'alert');
        box.style.cssText = 'padding:12px;margin:12px 0;color:#8b2020;';
        host.prepend(box);
      }
      box.textContent = message;
    }
  }

  function syncTerms(cart = lastCart) {
    const footer = document.querySelector('#main-cart-footer[data-id]');
    const checkbox = footer?.querySelector('#terms-checkbox');
    if (checkbox && acceptedTerms) checkbox.checked = true;
    const button = footer?.querySelector('button[name="checkout"]');
    if (button) button.disabled = (checkbox ? !checkbox.checked : false) || (cart ? cart.item_count === 0 : false);
  }

  async function refresh(cart) {
    const targets = [];
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const node = document.querySelector(`[id="${id}"][data-id]`);
      if (node) targets.push({ node, section: node.dataset.id, selector: `[id="${id}"][data-id]` });
    }
    for (const id of ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble']) {
      const node = document.getElementById(id);
      if (node) targets.push({ node, section: id, selector: '.shopify-section' });
    }
    if (targets.length) {
      const url = new URL(location.href);
      url.searchParams.set('sections', [...new Set(targets.map(t => t.section))].join(','));
      const response = await rawFetch(url.href, { cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) throw new Error('Cart display could not be refreshed');
      const sections = await response.json();
      // Validate all fragments before applying them.
      const fragments = targets.map(target => {
        const html = sections[target.section];
        if (!html) throw new Error('Missing cart section');
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = parsed.querySelector(target.selector);
        if (!content) throw new Error('Missing cart content');
        return { target, html: content.innerHTML };
      });
      for (const { target, html } of fragments) target.node.innerHTML = html;
    }
    document.querySelectorAll('cart-items, #main-cart-footer, form.mini-cart').forEach(node => {
      node.classList.toggle('is-empty', cart.item_count === 0);
    });
    syncTerms(cart);
    syncGiftMessage(cart);
  }

  function syncGiftMessage(cart) {
    const config = document.getElementById('tayshaba-gift-message');
    const pageForm = document.querySelector('form.cart__contents');
    let message = document.getElementById('gift-message');
    const hasGift = rules.some(rule => cart.items.some(item => isBottle(item, rule)) &&
      cart.items.some(item => Number(item.variant_id || item.id) === rule.giftVariantId));
    if (hasGift && pageForm && config) {
      if (!message) {
        message = document.createElement('div');
        message.id = 'gift-message';
        pageForm.before(message);
      }
      // Preserve the original banner element, position and visual styling.
      message.style.padding = '10px';
      message.style.background = '#e0ffe0';
      message.style.color = '#007700';
      message.style.marginTop = '20px';
      message.textContent = JSON.parse(config.textContent);
    } else if (message) message.remove();
  }

  async function settle() {
    while (externalWrites || Date.now() < editUntil) {
      if (Date.now() < editUntil) await delay(Math.min(50, editUntil - Date.now()));
      else { await queue; await delay(20); }
    }
    await queue;
    // Allow the existing theme to finish rendering its mutation response.
    await delay(30);
  }

  async function reconcile() {
    const unavailable = new Set();
    for (let attempt = 0; attempt < 50; attempt++) {
      await settle();
      const revision = writeRevision;
      const cart = await json('cart.js');
      if (externalWrites || Date.now() < editUntil || revision !== writeRevision) continue;
      const action = plan(cart, unavailable);
      if (!action) {
        lastCart = cart;
        if (renderNeeded) { await refresh(cart); renderNeeded = false; }
        else { syncTerms(cart); syncGiftMessage(cart); }
        // A shopper may have edited the cart while its HTML was loading.
        if (externalWrites || Date.now() < editUntil || revision !== writeRevision) {
          renderNeeded = true;
          continue;
        }
        notice(unavailable.size ? messages.stock : '');
        return cart;
      }
      // Mark before writing so a failed refresh is retried on the next validation.
      renderNeeded = true;
      try { await json(action.path, action.body); }
      catch (error) {
        if (!action.addId) throw error; // Failed removal must block normal checkout.
        unavailable.add(action.addId);
        notice(messages.stock);
        console.error('Tayshaba gift could not be added:', error);
      }
    }
    throw new Error('Cart did not settle');
  }

  function validate() {
    if (!running) running = reconcile().finally(() => { running = null; });
    return running;
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(() => validate().catch(error => {
      console.error('Tayshaba gift validation:', error);
      notice(messages.error);
    }), 80);
  }

  function cartForm(form) {
    return form instanceof HTMLFormElement && (form.matches('form.mini-cart, form.cart__contents'));
  }

  async function checkout(form) {
    if (checkingOut) return;
    checkingOut = true;
    const drawer = form.matches('form.mini-cart');
    try {
      const cart = await validate();
      if (!cart.item_count) { notice(messages.empty); return; }
      const liveForm = document.querySelector(drawer ? 'form.mini-cart' : 'form.cart__contents');
      if (!liveForm) throw new Error('Missing cart form');
      const terms = drawer ? null : document.querySelector('#main-cart-footer[data-id] #terms-checkbox');
      if (terms && !terms.checked) { notice(messages.terms); return; }
      // Native checkout should use the reconciled server cart, not old updates[] fields.
      const data = new FormData(liveForm);
      const submission = document.createElement('form');
      submission.method = 'post';
      submission.action = root + 'cart';
      submission.hidden = true;
      for (const [name, value] of data) {
        if (name === 'updates[]' || name === 'checkout' || typeof value !== 'string') continue;
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = name; input.value = value;
        submission.append(input);
      }
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = 'checkout'; input.value = '1';
      submission.append(input);
      document.body.append(submission);
      HTMLFormElement.prototype.submit.call(submission);
    } catch (error) {
      console.error('Tayshaba checkout stopped:', error);
      notice(messages.error);
    } finally { checkingOut = false; }
  }

  // Capture before theme click handlers, including checkout buttons outside the form.
  document.addEventListener('click', event => {
    const button = event.target.closest?.('button[name="checkout"], input[name="checkout"]');
    if (!button) return;
    const form = button.closest('form.mini-cart') ||
      (button.closest('#main-cart-footer') ? document.querySelector('form.cart__contents') : button.form);
    if (!cartForm(form)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    void checkout(form);
  }, true);

  document.addEventListener('submit', event => {
    if (!cartForm(event.target)) return;
    // Respect an explicit Update action. Implicit Enter submits go through validation.
    if (event.submitter && event.submitter.name !== 'checkout') return;
    event.preventDefault(); event.stopImmediatePropagation();
    void checkout(event.target);
  }, true);

  document.addEventListener('change', event => {
    if (event.target.id === 'terms-checkbox') {
      acceptedTerms = event.target.checked;
      syncTerms();
    }
    if (event.target.matches?.('cart-items [name="updates[]"], cart-note textarea, gift-note textarea')) {
      editUntil = Date.now() + 500; // Theme quantity debounce is 300 ms.
      schedule();
    }
  }, true);

  document.addEventListener('cart:updated', schedule);
  document.addEventListener('cartdrawer:opened', schedule);
  window.addEventListener('pageshow', schedule);
  window.TayshabaGiftGuard = { validate, rules: rules.map(rule => ({ ...rule })), plan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();
})();